<script setup lang="ts">
  import { CalendarDate } from '@internationalized/date';
  import type { DateValue } from '@internationalized/date';
  import dayjs from 'dayjs';
  import { computed, ref, watch } from 'vue';

  import type { Invoice, Task } from '@stuut/api/models';
  import type { HttpError } from '@stuut/common/src/api_client';
  import { Button } from '@ui/components/button';
  import { Calendar } from '@ui/components/calendar';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';

  interface Props {
    invoice?: Invoice;
    isDateHovered?: boolean;
    promiseData?: Record<string, any>;
    task: Task;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'afterSetDate', Invoice?: Invoice): void;
  }>();

  const isSaving = ref(false);
  const isDatePickerOpen = ref(false);

  const toCalendarDate = (dayjsDate: dayjs.Dayjs): CalendarDate => {
    return new CalendarDate(dayjsDate.year(), dayjsDate.month() + 1, dayjsDate.date());
  };

  const toCalendarString = (value?: string | Date | dayjs.Dayjs | null): string | null => {
    if (!value) return null;

    const date = dayjs(value);
    return date.isValid() ? toCalendarDate(date)?.toString() : null;
  };

  const promiseToPayDate = ref<DateValue | null>(
    props.invoice?.promised_to_pay_date
      ? toCalendarDate(dayjs(props.invoice?.promised_to_pay_date))
      : null,
  );

  const originalInvoiceDate = ref<string | null>(
    toCalendarString(props.invoice?.promised_to_pay_date),
  );

  const isDateMatchPromise = computed(() => {
    const invoicePromiseDate = props.invoice?.promised_to_pay_date;
    const taskPromiseDate = props.promiseData?.promised_date;
    if (!invoicePromiseDate || !taskPromiseDate) return false;
    return dayjs(invoicePromiseDate).isSame(dayjs(taskPromiseDate), 'day');
  });

  const savePromisedPayment = async () => {
    isSaving.value = true;
    try {
      await stuut.invoices.update(
        props.invoice?.id!,
        {
          data: {
            promised_to_pay_date: promiseToPayDate.value?.toString() || null,
          },
        },
        props.invoice,
      );

      originalInvoiceDate.value = promiseToPayDate.value?.toString() || null;
      isDatePickerOpen.value = false;
      toast.success('Updated promised payment date');
      emit('afterSetDate', props.invoice);
    } catch (err) {
      toast.error((err as HttpError).message || 'An error occurred.');
    } finally {
      isSaving.value = false;
    }
  };

  const clearDate = () => {
    promiseToPayDate.value = null;
    savePromisedPayment();
  };

  watch(isDatePickerOpen, () => {
    promiseToPayDate.value = props.invoice?.promised_to_pay_date
      ? toCalendarDate(dayjs(props.invoice?.promised_to_pay_date))
      : null;
  });

  watch(
    () => props.invoice?.promised_to_pay_date,
    (newDate) => {
      originalInvoiceDate.value = toCalendarString(newDate);
    },
  );

  defineExpose({
    isDatePickerOpen: isDatePickerOpen.value,
    openDatePicker: () => {
      isDatePickerOpen.value = true;
    },
  });
</script>

<template>
  <Popover v-model:open="isDatePickerOpen">
    <div
      class="flex gap-x-1 items-center p-1.5 rounded"
      :class="isDateHovered && 'bg-primary-muted/60'"
    >
      <PopoverTrigger as-child>
        <Timestamp
          :time="invoice?.promised_to_pay_date"
          class="justify-start hover:bg-transparent w-fit text-xs!"
          :class="!invoice?.promised_to_pay_date && 'font-bold'"
          fallback="Set Date"
          format="MMM D YYYY"
          date-only
          side="top"
          :editable="!!invoice?.id"
        />
      </PopoverTrigger>

      <template v-if="promiseData?.is_promised_date_updated">
        <Tooltip v-if="isDateMatchPromise">
          <TooltipTrigger as-child>
            <i class="fa-regular cursor-pointer fa-circle-question text-xs"></i>
          </TooltipTrigger>

          <TooltipContent>
            <div v-if="promiseData?.is_calculated" class="space-y-0.5">
              <strong
                >Date estimated using
                {{ dayjs(promiseData?.sent_date).isBefore(dayjs(), 'day') ? 'sent' : 'send' }}
                date</strong
              ><br />

              <div>
                {{ dayjs(promiseData?.sent_date)?.format('MMM D YYYY') }}
              </div>
            </div>

            <div v-else>Promised payment date provided by customer</div>
          </TooltipContent>
        </Tooltip>

        <Tooltip v-else>
          <TooltipTrigger as-child>
            <i class="fa-solid fa-triangle-exclamation text-warning text-xs"></i>
          </TooltipTrigger>

          <TooltipContent>
            <div class="space-y-0.5">
              <strong>Promised payment date was updated</strong>

              <div>
                Original value:
                {{
                  promiseData?.promised_date
                    ? dayjs(promiseData?.promised_date)?.format('MMM D YYYY')
                    : 'N/A'
                }}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </template>
    </div>

    <PopoverContent class="w-auto p-3 space-y-3">
      <!-- @vue-ignore -->
      <Calendar v-model="promiseToPayDate" calendar-label="Promise To Pay" />

      <div class="flex flex-row gap-x-2">
        <Button
          variant="outline"
          size="sm"
          class="flex-1"
          :loading="isSaving"
          :disabled="isSaving"
          @click="clearDate"
        >
          Clear
        </Button>

        <Button
          size="sm"
          class="flex-1"
          :loading="isSaving"
          :disabled="isSaving"
          @click="savePromisedPayment"
        >
          Save
        </Button>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped></style>
